import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { BackButton } from "@/common/BackButton";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import {
  CustomSectionTitle,
  CustomLoading,
} from "@ippon/shared-components";
import type { AppDispatch } from "@/store";
import {
  fetchManagementTaskById,
  type ManagementTaskRecord,
} from "@/store/managementTaskSlice";
import {
  fetchTaskResults,
  upsertTaskResult,
  type ManagementTaskResultRecord,
  type UpsertTaskResultBody,
} from "@/store/managementTaskResultSlice";
import ManagementResultEditDrawer from "@/components/PropertyDetail/InspectionCleaning/drawers/ManagementResultEditDrawer";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";

interface Props {
  /** PreviewDrawer 等で埋め込む際に使用する task_id (URL params より優先) */
  overrideTaskId?: string;
  /** PreviewDrawer 経由の property_id 指定 (querystring より優先) */
  overridePropertyId?: string;
  /** PreviewDrawer 経由の year 指定 (querystring より優先) */
  overrideYear?: number;
  /** PreviewDrawer 経由の month 指定 (querystring より優先) */
  overrideMonth?: number;
  embedded?: boolean;
  /** 保存成功後コールバック (親で一覧再取得等) */
  onUpdated?: () => void;
  /** ロード成功時に呼ばれる (ドロワー埋め込み時にパンくず title 取得に使う) */
  onLoaded?: (task: ManagementTaskRecord) => void;
}

const buildInitial = (
  rec: ManagementTaskResultRecord | undefined,
): {
  planned: boolean;
  plannedDates?: number[];
  completed: boolean;
  executionRecords?: any[];
  totalJudgement?: any;
  remarks?: string;
  plannedIsRemote?: boolean;
  actualIsRemote?: boolean;
  customFields?: any[];
} => {
  if (!rec) {
    // 該当月の実績レコード未作成 = デフォルト状態 (未予定 / 未実施)
    return { planned: false, completed: false };
  }
  return {
    planned: !!rec.planned,
    plannedDates: rec.planned_dates,
    completed: !!rec.completed,
    plannedIsRemote: !!rec.planned_is_remote,
    actualIsRemote: !!rec.actual_is_remote,
    executionRecords: rec.execution_records,
    totalJudgement: rec.total_judgement,
    remarks: rec.remarks,
    customFields: (rec.custom_fields || []).map((c: any) => ({
      field_id: c.field_id,
      value: c.value as never,
    })),
  };
};

export const ManagementTaskResultDetailPage: React.FC<Props> = ({
  overrideTaskId,
  overridePropertyId,
  overrideYear,
  overrideMonth,
  embedded,
  onUpdated,
  onLoaded,
}) => {
  const params = useParams<{ task_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();

  const taskId = overrideTaskId || params?.task_id || "";
  const propertyId =
    overridePropertyId || searchParams.get("property_id") || "";
  const year =
    overrideYear ??
    (searchParams.get("year") ? Number(searchParams.get("year")) : NaN);
  const month =
    overrideMonth ??
    (searchParams.get("month") ? Number(searchParams.get("month")) : NaN);

  const [task, setTask] = useState<ManagementTaskRecord | null>(null);
  const [result, setResult] = useState<ManagementTaskResultRecord | undefined>(
    undefined,
  );
  const dataRef = useRef<ManagementTaskRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoadError, setInitialLoadError] =
    useState<ApiErrorType | null>(null);

  useEffect(() => {
    dataRef.current = task;
  }, [task]);

  // ManagementResultEditDrawer 内の同期 useEffect が `[open, initial]` 依存なので、
  // initial の参照を result 変化時にのみ更新する (毎レンダー new object だと
  // drawer の state が毎回 reset され、ユーザー入力消失 + 新月反映ちらつきの原因)。
  const initial = useMemo(() => buildInitial(result), [result]);

  // onLoaded を ref 経由で呼ぶ。fetchDetail の useCallback 依存から onLoaded を外し、
  // 親の handleLoaded が new ref になっても fetch ループにならないようにする。
  const onLoadedRef = useRef(onLoaded);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);

  const fetchDetail = useCallback(async () => {
    setInitialLoadError(null);
    if (!taskId || !propertyId || !Number.isFinite(year) || !Number.isFinite(month)) {
      // 必須パラメータ不足 (直リンで querystring 落ち等)
      setInitialLoadError("notFound");
      return;
    }
    setLoading(true);
    try {
      // 注: fetchTaskResults.fulfilled は state.list.data を**置換**するため、
      // task_id + month で絞り込むと親のテーブルの他月の丸が消える。
      // 親と同じ「年単位 fetch」を呼んで全置換することで親の表も整合させ、
      // page 側は client filter で対象月を抽出する。
      const [taskRec, results] = await Promise.all([
        dispatch(
          fetchManagementTaskById({ property_id: propertyId, task_id: taskId }),
        ).unwrap(),
        dispatch(
          fetchTaskResults({ property_id: propertyId, year }),
        ).unwrap(),
      ]);
      setTask(taskRec);
      onLoadedRef.current?.(taskRec);
      const found = (results as ManagementTaskResultRecord[]).find(
        (r) =>
          r.task_id === taskId && r.year === year && r.month === month,
      );
      setResult(found);
    } catch (err) {
      const payload = err as ApiErrorPayload | undefined;
      const errorType = payload?.errorType ?? "unknown";
      setInitialLoadError(errorType);
      if (errorType === "notFound" || !dataRef.current) {
        setTask(null);
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, taskId, propertyId, year, month]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // パンくず最終項目に "YYYY年MM月 (業務名)" を反映
  const lastBreadcrumbTitleRef = useRef<string>("");
  useEffect(() => {
    if (embedded) return;
    if (!task) return;
    if (!Number.isFinite(year) || !Number.isFinite(month)) return;
    const title = `${year}年${String(month).padStart(2, "0")}月`;
    if (title === lastBreadcrumbTitleRef.current) return;
    lastBreadcrumbTitleRef.current = title;
    const currentState = (location.state as Record<string, unknown>) || {};
    navigate(
      `/management-task-results/${taskId}${location.search || ""}`,
      {
        state: { ...currentState, title, taskName: task.name },
        replace: true,
      },
    );
  }, [
    task,
    taskId,
    year,
    month,
    embedded,
    navigate,
    location.state,
    location.search,
  ]);

  const handleSave = async (body: UpsertTaskResultBody) => {
    try {
      await dispatch(upsertTaskResult(body)).unwrap();
      showToast({ type: "success", message: "業務実績を保存しました" });
      onUpdated?.();
      // 保存後に再フェッチで最新化 (ローカルダーティ解除のため)
      await fetchDetail();
    } catch (e) {
      const msg = typeof e === "string" ? e : "業務実績の保存に失敗しました";
      showToast({ type: "error", message: msg });
      throw e;
    }
  };

  const hasData = Boolean(task);
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasData,
    dedupeKey: `management-task-result:${taskId}:${year}:${month}`,
  });

  if (shouldShowInitialLoadError(initialLoadError, hasData)) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchDetail} />
    );
  }

  if (loading || !task) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          minHeight: "60vh",
          width: "100%",
        }}
      >
        <CustomLoading loading={true} fullScreen={false} size="medium" />
      </Box>
    );
  }

  const title = `${task.name} (${year}年${month}月)`;

  return (
    <Box pb={9} pt={0} px={embedded ? 1.5 : 0}>
      <Box
        sx={{
          mb: 3,
          // タイトルを上端に sticky 固定。ドロワー(embedded)はスクロール先頭(0)、
          // 単独ページ(全画面)は固定ヘッダー(50px)の下に貼り付ける。
          // 上余白は sticky 要素の内側 pt で持たせる (外側だとスクロール時に動く)。
          pt: embedded ? 0 : 0.5,
          position: "sticky",
          top: embedded ? 0 : 50,
          zIndex: 10,
          backgroundColor: "white",
        }}
      >
        <CustomSectionTitle
          title={title}
          size="large"
          addBorder
          icon={!embedded ? <BackButton size={24} /> : undefined}
        />
      </Box>

      <ManagementResultEditDrawer
        open
        onClose={() => {
          if (!embedded) navigate(-1);
        }}
        taskName={task.name}
        property_id={propertyId}
        task_id={taskId}
        year={year}
        month={month}
        frequency={task.frequency}
        taskType={task.task_type}
        initial={initial}
        onSave={handleSave}
        displayMode="page"
        embedded={embedded}
      />
    </Box>
  );
};

export default ManagementTaskResultDetailPage;
